import { AlertTriangle, Check, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type PlanLimit = {
  code: string;
  message: string;
  detail?: string | null;
  /** Generation argument that would lift this cap; null when nothing can. */
  option?: string | null;
  suggestedValue?: unknown;
  lost?: number | null;
};

export type PlannerOverrides = {
  maxNewSkills?: number | null;
  includeDistantReview?: boolean | null;
};

type Props = {
  requested: number;
  planned: number;
  limits: PlanLimit[];
  overrides: PlannerOverrides;
  onApply: (next: PlannerOverrides) => void;
  onReset: () => void;
  className?: string;
};

/** Lithuanian needs three forms; 11-19 take the genitive plural like 10+. */
function tasksLabel(n: number) {
  const last = n % 10;
  const teens = n % 100 >= 11 && n % 100 <= 19;
  if (last === 1 && !teens) return `${n} užduotis`;
  if (last >= 2 && last <= 9 && !teens) return `${n} užduotys`;
  return `${n} užduočių`;
}

/**
 * Turns one limit into the button that lifts it. A cap a grown-up cannot
 * change gets no button — describing a rule and offering nothing is honest,
 * offering a button that does nothing is not.
 */
function overrideFor(limit: PlanLimit): PlannerOverrides | null {
  if (limit.option === "maxNewSkills" && typeof limit.suggestedValue === "number") {
    return { maxNewSkills: limit.suggestedValue };
  }
  if (limit.option === "includeDistantReview" && limit.suggestedValue === true) {
    return { includeDistantReview: true };
  }
  return null;
}

function overrideLabel(limit: PlanLimit): string {
  if (limit.option === "maxNewSkills") return `Leisti ${limit.suggestedValue} naujas temas`;
  return "Įtraukti tolimesnį kartojimą";
}

/**
 * Says what a session of the chosen length will actually come out as, while the
 * choice is still being made.
 *
 * The planner hands each skill a fixed ration and visits it once, so a learner
 * without many unlocked skills caps out well below a long session — 15 of 20, or
 * 4 of 10 on a first session. Before this, that arrived as a silently shorter
 * session, which reads as a broken generator rather than as the pedagogy working
 * as designed. Each cause carries the control that lifts it, so the grown-up can
 * disagree with a rule in the moment rather than go looking for a setting.
 */
export function SessionLengthForecast({
  requested,
  planned,
  limits,
  overrides,
  onApply,
  onReset,
  className,
}: Props) {
  const overridden =
    (overrides.maxNewSkills != null && overrides.maxNewSkills !== 1) ||
    overrides.includeDistantReview === true;
  const short = planned < requested;

  if (!short && !overridden) {
    return (
      <p className={cn("flex items-center gap-1.5 text-sm text-muted-foreground", className)}>
        <Check className="h-4 w-4 text-emerald-600" />
        Bus sugeneruota {tasksLabel(planned)}.
      </p>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border p-3 text-sm",
        short
          ? "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"
          : "border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40",
        className
      )}
    >
      <p className="flex items-center gap-1.5 font-medium">
        {short ? (
          <AlertTriangle className="h-4 w-4 text-amber-600" />
        ) : (
          <Check className="h-4 w-4 text-emerald-600" />
        )}
        {short
          ? `Bus sugeneruota ${tasksLabel(planned)} iš ${requested} — kodėl:`
          : `Bus sugeneruota ${tasksLabel(planned)}.`}
      </p>

      {short && (
        <ul className="mt-2 space-y-1.5">
          {limits.map((limit) => {
            const next = overrideFor(limit);
            return (
              <li key={limit.code} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-muted-foreground">
                  • {limit.message}
                  {limit.detail ? ` (${limit.detail})` : ""}
                </span>
                {next && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => onApply(next)}
                  >
                    <Wand2 className="mr-1 h-3 w-3" />
                    {overrideLabel(limit)}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {overridden && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          Taisyklės pakeistos šiai generacijai:
          {overrides.maxNewSkills != null && overrides.maxNewSkills !== 1 && (
            <span className="rounded bg-background px-1.5 py-0.5">
              {overrides.maxNewSkills} naujos temos
            </span>
          )}
          {overrides.includeDistantReview && (
            <span className="rounded bg-background px-1.5 py-0.5">tolimesnis kartojimas</span>
          )}
          <button type="button" className="underline hover:no-underline" onClick={onReset}>
            Grąžinti įprastas
          </button>
        </p>
      )}
    </div>
  );
}
